import { communityService } from '../service/community-service'
import {
  AdminCommentListParams,
  AdminCommentsListResponse,
  CommunityComment,
  CommunityCommentsResponse,
  CommunityEntityType,
  CommunityRepliesResponse,
  CreateCommentPayload,
  ReactionPayload,
  ReactionSummary,
  ReactionTypeDefinition,
  UpdateAdminCommentPayload,
} from '../types/community'
import { queryKeys } from '../util/query-keys'
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query'
import { queryClient } from 'src/query-client'

type SortBy = 'recent' | 'oldest'

export const useComments = (params: {
  entityType: CommunityEntityType
  entityId: string
  sortBy?: SortBy
}): UseInfiniteQueryResult<CommunityCommentsResponse, Error> => {
  const { entityType, entityId, sortBy = 'recent' } = params
  return useInfiniteQuery<CommunityCommentsResponse, Error>({
    queryKey: queryKeys.comments(entityType, entityId, sortBy),
    queryFn: ({ pageParam = 1 }) =>
      communityService.listComments({
        entityType,
        entityId,
        page: pageParam,
        pageSize: 20,
        sortBy,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page >= lastPage.pagination.totalPages) return undefined
      return lastPage.pagination.page + 1
    },
  }) as UseInfiniteQueryResult<CommunityCommentsResponse, Error>
}

export const useReplies = (commentId: string, enabled = true): UseInfiniteQueryResult<CommunityRepliesResponse, Error> => {
  return useInfiniteQuery<CommunityRepliesResponse, Error>({
    queryKey: queryKeys.commentReplies(commentId),
    queryFn: ({ pageParam = 1 }) =>
      communityService.listReplies({
        commentId,
        page: pageParam,
        pageSize: 10,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page >= lastPage.pagination.totalPages) return undefined
      return lastPage.pagination.page + 1
    },
    enabled: !!commentId && enabled,
  }) as UseInfiniteQueryResult<CommunityRepliesResponse, Error>
}

export const useCreateComment = (): UseMutationResult<CommunityComment, Error, { payload: CreateCommentPayload }> => {
  return useMutation<CommunityComment, Error, { payload: CreateCommentPayload }>({
    mutationFn: ({ payload }) => communityService.createComment(payload),
    onSuccess: (comment) => {
      // Invalidate the main comments query (use partial key to match all sortBy variants)
      queryClient.invalidateQueries({
        queryKey: ['community', 'comments', comment.entityType, comment.entityId],
        exact: false,
      })

      // If this is a reply, also invalidate the parent comment's replies query
      if (comment.parentCommentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.commentReplies(comment.parentCommentId),
          exact: false,
        })
      }
    },
  })
}

export const useDeleteComment = (): UseMutationResult<
  void,
  Error,
  { commentId: string; entityType: CommunityEntityType; entityId: string }
> => {
  return useMutation<void, Error, { commentId: string; entityType: CommunityEntityType; entityId: string }>({
    mutationFn: ({ commentId }) => communityService.deleteComment(commentId),
    onSuccess: (_data, variables) => {
      // Use partial key to match all sortBy variants
      queryClient.invalidateQueries({
        queryKey: ['community', 'comments', variables.entityType, variables.entityId],
        exact: false,
      })
    },
  })
}

export const useReactions = (params: {
  entityType: CommunityEntityType
  entityId: string
}): UseQueryResult<ReactionSummary, Error> => {
  return useQuery<ReactionSummary, Error>({
    queryKey: queryKeys.reactions(params.entityType, params.entityId),
    queryFn: () => communityService.getReactions(params),
    enabled: !!params.entityId,
  })
}

export const useToggleReaction = (): UseMutationResult<ReactionSummary, Error, { payload: ReactionPayload }> => {
  return useMutation<ReactionSummary, Error, { payload: ReactionPayload }>({
    mutationFn: ({ payload }) => communityService.toggleReaction(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKeys.reactions(variables.payload.entityType, variables.payload.entityId), data)
    },
  })
}

export const useReactionTypes = (): UseQueryResult<ReactionTypeDefinition[], Error> => {
  return useQuery<ReactionTypeDefinition[], Error>({
    queryKey: queryKeys.reactionTypes(),
    queryFn: () => communityService.getReactionTypes(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour (reaction types rarely change)
  })
}

export const useAdminComments = (params: AdminCommentListParams): UseQueryResult<AdminCommentsListResponse, Error> => {
  return useQuery<AdminCommentsListResponse, Error>({
    queryKey: queryKeys.adminComments({
      offset: params.offset,
      search: params.search,
      entityType: params.entityType,
    }),
    queryFn: () => communityService.adminListComments(params),
    keepPreviousData: true,
    retry: (failureCount, error) => ((error as any)?.response?.status === 403 ? false : failureCount < 3),
  })
}

export const useUpdateAdminComment = (): UseMutationResult<
  CommunityComment,
  Error,
  { commentId: string; payload: UpdateAdminCommentPayload }
> => {
  return useMutation<CommunityComment, Error, { commentId: string; payload: UpdateAdminCommentPayload }>({
    mutationFn: ({ commentId, payload }) => communityService.adminUpdateComment(commentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-comments'], exact: false })
    },
  })
}

export const useDeleteAdminComment = (): UseMutationResult<void, Error, string> => {
  return useMutation<void, Error, string>({
    mutationFn: (commentId) => communityService.adminDeleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-comments'], exact: false })
    },
  })
}
